"use client";

import { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import useNavItems from "@/app/hooks/nav-items.hook";
import {
    getActiveNavLogoHref,
    getActiveNavLogoUrl,
} from "@/app/utils/get-nav-logo-url";
import { DirectPaths } from "@/config/routes";
import { useAuth } from "@/context/authcontext";
import { useNavMobileTheme, useNavTheme } from "./nav-theme-context";

type NavBrandLogoProps = {
    active?: string;
    className?: string;
    /** Use mobile product logo (e.g. broking mobile mark) when available. */
    forMobile?: boolean;
};

const toWhiteLogoUrl = (url: string) => {
    const original = url.substring(url.lastIndexOf("/"));
    return url.replace(original, "/white" + original);
};

const NavBrandLogo = ({
    active = "",
    className,
    forMobile = false,
}: NavBrandLogoProps) => {
    const desktopTheme = useNavTheme();
    const mobileTheme = useNavMobileTheme();
    const theme = forMobile ? mobileTheme : desktopTheme;
    const { isAuthenticated } = useAuth();
    const navItems = useNavItems({ active, isLoggedIn: Boolean(isAuthenticated) });

    const defaultHref = isAuthenticated ? DirectPaths.PLATFORM : DirectPaths.HOME;

    const logoUrl = useMemo(() => {
        const defaultLogo = process.env.ICM_LOGO_URL ?? "";
        const useWhiteTransform = theme === "blue";
        return getActiveNavLogoUrl(
            navItems,
            useWhiteTransform ? toWhiteLogoUrl(defaultLogo) : defaultLogo,
            useWhiteTransform ? toWhiteLogoUrl : undefined,
            forMobile,
        );
    }, [navItems, theme, forMobile]);

    const logoHref = useMemo(
        () => getActiveNavLogoHref(navItems, defaultHref),
        [navItems, defaultHref],
    );

    return (
        <Link
            href={logoHref}
            aria-label="InCred Money home"
            className="cursor-default"
        >
            <Image
                src={logoUrl}
                alt="InCred Logo"
                width={119}
                height={42}
                className={className ?? "h-[42px] w-auto shrink-0"}
            />
        </Link>
    );
};

export default NavBrandLogo;
